/**
 * Validates that a value is not empty
 * @param value Value to validate
 * @returns True if valid, false if invalid
 */
export const isNotEmpty = (value: any): boolean => {
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return value !== null && value !== undefined;
};

/**
 * Validates an email address
 * @param email Email to validate
 * @returns True if valid, false if invalid
 */
export const isValidEmail = (email: string): boolean => {
  // Simple regex for basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates a phone number
 * @param phone Phone number to validate
 * @returns True if valid, false if invalid
 */
export const isValidPhone = (phone: string): boolean => {
  // Simple regex for basic phone validation - accepts various formats
  const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
  return phoneRegex.test(phone.replace(/\s+/g, ''));
};

/**
 * Sanitizes a string by removing potentially dangerous characters
 * @param input String to sanitize
 * @returns Sanitized string
 */
export const sanitizeString = (input: string): string => {
  return input
    .trim()
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[^\w\s.,?!@:;()\-]/gi, ''); // Remove special chars except allowed ones
};

/**
 * Validates a contact form submission
 * @param formData Contact form data
 * @returns Object with validation errors, empty if no errors
 */
export const validateContactForm = (formData: any) => {
  const errors: Record<string, string> = {};

  if (!isNotEmpty(formData.name)) {
    errors.name = 'Name is required';
  }

  if (!isNotEmpty(formData.email)) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (formData.phone && !isValidPhone(formData.phone)) {
    errors.phone = 'Please enter a valid phone number';
  }

  if (!isNotEmpty(formData.message)) {
    errors.message = 'Message is required';
  }

  return errors;
};

/**
 * Sanitizes contact form data
 * @param formData Contact form data
 * @returns Sanitized form data
 */
export const sanitizeContactForm = (formData: any) => {
  return {
    ...formData,
    name: sanitizeString(formData.name),
    email: formData.email.trim().toLowerCase(),
    company: formData.company ? sanitizeString(formData.company) : '',
    phone: formData.phone ? formData.phone.trim() : '',
    message: sanitizeString(formData.message),
    service: Array.isArray(formData.services)
      ? formData.services.join(',')
      : '',
    best_time_to_contact: formData.best_time_to_contact
      ? sanitizeString(formData.best_time_to_contact)
      : '',
    how_found_us: formData.how_found_us
      ? sanitizeString(formData.how_found_us)
      : '',
    conversion_source: formData.conversion_source
      ? sanitizeString(formData.conversion_source)
      : 'contact_form',
  };
};

/**
 * Validates an email subscription
 * @param email Email to validate
 * @returns Object with validation errors, empty if no errors
 */
export const validateEmailSubscription = (email: string) => {
  const errors: Record<string, string> = {};

  if (!isNotEmpty(email)) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(email)) {
    errors.email = 'Please enter a valid email address';
  }

  return errors;
};
